import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useTrip } from '../contexts/TripContext';
import { useLanguage } from '../contexts/LanguageContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { TripCard } from '../components/trip/TripCard';
import { JourneyTimeline } from '../components/trip/JourneyTimeline';
import { TutorialOverlay, useTutorial } from '../components/common/TutorialOverlay';
import { OnboardingCards } from '../components/common/OnboardingCards';
import { GuidedTour } from '../components/common/GuidedTour';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Plus, Ticket, MapPin, Users, Calendar, TrendingUp, Loader2, HelpCircle, CheckCircle2, Clock, Lock, XCircle, Wallet, Eye, Car, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { differenceInDays, parseISO, isBefore } from 'date-fns';
import { ViewModeToggle, useViewMode, getGridClassName, VIEW_MODES } from '../components/common/ViewModeToggle';
import { WalletModal } from '../components/wallet/WalletModals';

export default function Dashboard() {
  const { user, api } = useAuth();
  const { trips, fetchTrips, joinTrip, loading } = useTrip();
  const { t } = useLanguage();
  const [inviteCode, setInviteCode] = useState('');
  const [joiningTrip, setJoiningTrip] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showGuidedTour, setShowGuidedTour] = useState(false);
  
  // View mode toggle
  const [viewMode, setViewMode] = useViewMode('dashboard-view-mode');
  
  // Tutorial
  const { showTutorial, checkTutorialStatus, startTutorial, closeTutorial, completeTutorial } = useTutorial();

  // Wallet state
  const [walletsSummary, setWalletsSummary] = useState([]);
  const [totalWalletBalance, setTotalWalletBalance] = useState(0);
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const [selectedWalletTrip, setSelectedWalletTrip] = useState(null);

  // Transport costs state
  const [transportCostsSummary, setTransportCostsSummary] = useState([]);
  const [totalTransportCosts, setTotalTransportCosts] = useState(0);

  // Fetch transport costs for all trips
  const fetchTransportCosts = async () => {
    if (!trips || trips.length === 0) return;
    
    try {
      let totalCosts = 0;
      const summaries = [];
      
      for (const trip of trips.slice(0, 5)) {
        try {
          // Fetch payments related to transport for this user
          const response = await api.get(`/trips/${trip.id}/payments`);
          const payments = response.data || [];
          
          // Filter transport payments for current user
          const transportPayments = payments.filter(p => 
            p.category === 'transport' && 
            p.member_payments?.some(mp => mp.user_id === user?.id)
          );
          
          if (transportPayments.length > 0) {
            let tripTotal = 0;
            let tripPaid = 0;
            let tripInstallments = 0;
            
            transportPayments.forEach(payment => {
              const myPayment = payment.member_payments?.find(mp => mp.user_id === user?.id);
              if (myPayment) {
                tripTotal += myPayment.amount || 0;
                if (myPayment.status === 'paid') {
                  tripPaid += myPayment.amount || 0;
                }
                tripInstallments++;
              }
            });
            
            if (tripTotal > 0) {
              summaries.push({
                tripId: trip.id,
                tripName: trip.name,
                total: tripTotal,
                paid: tripPaid,
                pending: tripTotal - tripPaid,
                installments: tripInstallments,
                currency: trip.settings?.currency || 'BRL'
              });
              totalCosts += tripTotal;
            }
          }
        } catch (e) {
          // Skip trips where user is not a member or has no transport
        }
      }
      
      setTransportCostsSummary(summaries);
      setTotalTransportCosts(totalCosts);
    } catch (error) {
      console.error('Error fetching transport costs:', error);
    }
  };

  // Fetch wallets for all trips
  const fetchAllWallets = async () => {
    if (!trips || trips.length === 0) return;
    
    try {
      let total = 0;
      const summaries = [];
      
      for (const trip of trips.slice(0, 5)) { // Limit to 5 trips for performance
        try {
          const response = await api.get(`/trips/${trip.id}/wallets/me`);
          if (response.data && response.data.balance !== 0) {
            summaries.push({
              tripId: trip.id,
              tripName: trip.name,
              ...response.data
            });
            total += response.data.balance;
          }
        } catch (e) {
          // Skip trips where user is not a member
        }
      }
      
      setWalletsSummary(summaries);
      setTotalWalletBalance(total);
    } catch (error) {
      console.error('Error fetching wallets:', error);
    }
  };

  useEffect(() => {
    fetchTrips();
    checkTutorialStatus();
    
    // Check if new user
    if (user?.is_new_user) {
      setShowOnboarding(true);
    }
  }, [fetchTrips, checkTutorialStatus, user?.is_new_user]);

  // Fetch wallets after trips are loaded
  useEffect(() => {
    if (trips.length > 0) {
      fetchAllWallets();
      fetchTransportCosts();
    }
  }, [trips]);

  const handleOnboardingComplete = async () => {
    setShowOnboarding(false);
    setShowGuidedTour(true);
    
    // Mark onboarding as complete
    try {
      await api.post('/auth/complete-onboarding');
    } catch (error) {
      console.log('Error completing onboarding:', error);
    }
  };

  const handleGuidedTourComplete = () => {
    setShowGuidedTour(false);
    toast.success('Pronto! Agora você pode criar sua primeira viagem! 🚀');
  };

  // Join result modal state
  const [joinResult, setJoinResult] = useState(null);
  const [joinResultModalOpen, setJoinResultModalOpen] = useState(false);

  const handleJoinTrip = async (e) => {
    e.preventDefault();
    if (!inviteCode.trim()) return;

    setJoiningTrip(true);
    try {
      const result = await joinTrip(inviteCode.trim().toUpperCase());
      
      // Check if pending approval or direct access
      if (result.status === 'pending') {
        setJoinResult({
          type: 'pending',
          tripName: result.trip_name,
          message: result.message
        });
        setJoinResultModalOpen(true);
      } else if (result.status === 'approved') {
        setJoinResult({
          type: 'approved',
          tripName: result.trip_name,
          tripId: result.trip_id,
          message: result.message
        });
        setJoinResultModalOpen(true);
        fetchTrips(); // Refresh trips list
      }
      
      setInviteCode('');
      setDialogOpen(false);
    } catch (error) {
      const message = error.response?.data?.detail || 'Código inválido';
      
      // Check for specific error types
      if (message.includes('fechada')) {
        setJoinResult({
          type: 'closed',
          message: message
        });
        setJoinResultModalOpen(true);
      } else if (message.includes('encerrada')) {
        setJoinResult({
          type: 'ended',
          message: message
        });
        setJoinResultModalOpen(true);
      } else {
        // Show error in modal instead of toast
        setJoinResult({
          type: 'error',
          message: message
        });
        setJoinResultModalOpen(true);
      }
    } finally {
      setJoiningTrip(false);
    }
  };

  // Find next upcoming trip
  const upcomingTrips = trips
    .filter(t => {
      const startDate = typeof t.start_date === 'string' ? parseISO(t.start_date) : t.start_date;
      return isBefore(new Date(), startDate) && t.status !== 'cancelled';
    })
    .sort((a, b) => {
      const dateA = typeof a.start_date === 'string' ? parseISO(a.start_date) : a.start_date;
      const dateB = typeof b.start_date === 'string' ? parseISO(b.start_date) : b.start_date;
      return dateA - dateB;
    });

  const nextTrip = upcomingTrips[0];

  // Stats
  const totalTrips = trips.length;
  const activeTrips = trips.filter(t => t.status === 'planning' || t.status === 'confirmed').length;
  const totalMembers = trips.reduce((acc, t) => acc + (t.member_count || 0), 0);

  return (
    <DashboardLayout title="Dashboard">
      {/* Onboarding Cards (for new users) */}
      {showOnboarding && (
        <OnboardingCards 
          onComplete={handleOnboardingComplete}
          onSkip={() => {
            setShowOnboarding(false);
            handleOnboardingComplete();
          }}
        />
      )}

      {/* Guided Tour (after onboarding) */}
      {showGuidedTour && (
        <GuidedTour onComplete={handleGuidedTourComplete} />
      )}

      {/* Tutorial Overlay (for returning users) */}
      {showTutorial && (
        <TutorialOverlay onClose={closeTutorial} onComplete={completeTutorial} />
      )}
      
      <div className="space-y-6">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        >
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-slate-900 dark:text-white">
              {t('dashboard.greeting')}, {user?.name?.split(' ')[0]}! {user?.avatar_emoji || '👋'}
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              {nextTrip
                ? t('dashboard.nextTripComing')
                : t('dashboard.planNextAdventure')}
            </p>
          </div>

          <div className="flex gap-3">
            {/* Tutorial Button */}
            <Button 
              variant="ghost"
              size="icon"
              className="rounded-full" 
              onClick={startTutorial}
              title="Ver tutorial"
              data-testid="start-tutorial-btn"
            >
              <HelpCircle className="w-5 h-5 text-slate-500 dark:text-slate-400" />
            </Button>
            
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-full" data-testid="join-trip-btn" data-tour="join-trip-btn">
                  <Ticket className="w-4 h-4 mr-2" />
                  {t('dashboard.inviteCode')}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('dashboard.joinTripTitle')}</DialogTitle>
                  <DialogDescription>
                    {t('dashboard.joinTripDesc')}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleJoinTrip} className="space-y-4 mt-4">
                  <Input
                    placeholder="Ex: ABC123XY"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                    className="text-center text-lg tracking-widest uppercase"
                    maxLength={8}
                    data-testid="invite-code-input"
                  />
                  <Button
                    type="submit"
                    className="w-full rounded-full"
                    disabled={joiningTrip || !inviteCode.trim()}
                    data-testid="join-trip-submit"
                  >
                    {joiningTrip ? <Loader2 className="w-4 h-4 animate-spin" /> : t('dashboard.join')}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            <Link to="/trips/new">
              <Button className="rounded-full bg-orange-500 hover:bg-orange-600" data-testid="create-trip-btn" data-tour="create-trip-btn">
                <Plus className="w-4 h-4 mr-2" />
                {t('dashboard.newTrip')}
              </Button>
            </Link>
          </div>
        </motion.div>

        {/* Stats Cards - Tamanho uniforme no mobile */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-tour="dashboard-stats">
          {[
            { icon: MapPin, label: t('dashboard.totalTrips'), value: totalTrips, color: 'text-orange-500' },
            { icon: Calendar, label: t('dashboard.activeTrips'), value: activeTrips, color: 'text-sky-500' },
            { icon: Users, label: t('dashboard.participants'), value: totalMembers, color: 'text-violet-500' },
            { icon: TrendingUp, label: t('dashboard.upcoming'), value: upcomingTrips.length, color: 'text-green-500' },
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="border-slate-100 dark:border-slate-700 h-full">
                <CardContent className="p-3 sm:p-4 h-full">
                  <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 h-full">
                    <div className={`w-10 h-10 rounded-xl bg-slate-50 dark:bg-slate-800 flex items-center justify-center flex-shrink-0 ${stat.color}`}>
                      <stat.icon className="w-5 h-5" />
                    </div>
                    <div className="text-center sm:text-left">
                      <p className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">{stat.value}</p>
                      <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 leading-tight">{stat.label}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Wallet Summary Card */}
        {totalWalletBalance !== 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
          >
            <Card className="border-slate-100 dark:border-slate-700 bg-gradient-to-r from-orange-500/10 to-amber-500/10 dark:from-orange-500/20 dark:to-amber-500/20">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center">
                      <Wallet className="w-6 h-6 text-orange-500" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Saldo total em viagens</p>
                      <p className={`text-2xl font-bold ${totalWalletBalance >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalWalletBalance)}
                      </p>
                    </div>
                  </div>
                  {walletsSummary.length > 0 && (
                    <div className="text-right">
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                        {walletsSummary.length} viagem(ns) com saldo
                      </p>
                      <div className="flex flex-col gap-1">
                        {walletsSummary.slice(0, 2).map((w) => (
                          <button
                            key={w.tripId}
                            onClick={() => {
                              setSelectedWalletTrip({ tripId: w.tripId, tripName: w.tripName, userId: w.user_id, userName: user?.name });
                              setWalletModalOpen(true);
                            }}
                            className="text-xs text-orange-500 hover:text-orange-400 flex items-center gap-1 justify-end"
                          >
                            <Eye className="w-3 h-3" />
                            {w.tripName.substring(0, 15)}{w.tripName.length > 15 ? '...' : ''}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Transport Costs Summary Card */}
        {totalTransportCosts > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.28 }}
          >
            <Card className="border-slate-100 dark:border-slate-700 bg-gradient-to-r from-sky-500/10 to-blue-500/10 dark:from-sky-500/20 dark:to-blue-500/20">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-sky-500/20 flex items-center justify-center">
                      <Car className="w-6 h-6 text-sky-500" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">{t('dashboard.transportCosts')}</p>
                      <p className="text-2xl font-bold text-slate-900 dark:text-white">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalTransportCosts)}
                      </p>
                    </div>
                  </div>
                  {transportCostsSummary.length > 0 && (
                    <div className="text-right">
                      <div className="flex flex-col gap-1">
                        {transportCostsSummary.map((tc) => {
                          const isPaid = tc.paid >= tc.total;
                          const percentPaid = tc.total > 0 ? Math.round((tc.paid / tc.total) * 100) : 0;
                          return (
                            <div key={tc.tripId} className="text-xs">
                              <span className="text-slate-600 dark:text-slate-400">
                                {tc.tripName.substring(0, 15)}{tc.tripName.length > 15 ? '...' : ''}
                              </span>
                              <span className={`ml-2 font-medium ${isPaid ? 'text-green-500' : 'text-amber-500'}`}>
                                {isPaid ? (
                                  <span className="flex items-center gap-1 justify-end">
                                    <CheckCircle2 className="w-3 h-3" />
                                    {t('dashboard.paid')}
                                  </span>
                                ) : (
                                  `${tc.installments}x · ${percentPaid}%`
                                )}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Next Trip Timeline */}
        {nextTrip && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h2 className="font-heading font-bold text-lg text-slate-900 dark:text-white mb-4">{t('dashboard.nextTrip')}</h2>
            <Link to={`/trip/${nextTrip.id}`}>
              <JourneyTimeline
                tripId={nextTrip.id}
                startDate={nextTrip.start_date}
                endDate={nextTrip.end_date}
                tripName={nextTrip.name}
                destination={nextTrip.destination}
              />
            </Link>
          </motion.div>
        )}

        {/* My Trips - Future Only */}
        <div>
          {/* Desktop layout */}
          <div className="hidden sm:flex items-center justify-between mb-4">
            <h2 className="font-heading font-bold text-lg text-slate-900 dark:text-white">{t('dashboard.upcomingTrips')}</h2>
            <div className="flex items-center gap-3">
              <ViewModeToggle viewMode={viewMode} onViewModeChange={setViewMode} />
              <Link to="/trips" className="text-sm text-orange-600 hover:text-orange-700 font-medium">
                {t('common.viewAll')}
              </Link>
            </div>
          </div>
          
          {/* Mobile layout - Title centered, controls below */}
          <div className="sm:hidden mb-4">
            <h2 className="font-heading font-bold text-lg text-slate-900 dark:text-white text-center mb-3">{t('dashboard.upcomingTrips')}</h2>
            <div className="flex items-center justify-center gap-3">
              <ViewModeToggle viewMode={viewMode} onViewModeChange={setViewMode} />
              <Link to="/trips" className="text-sm text-orange-600 hover:text-orange-700 font-medium">
                {t('common.viewAll')}
              </Link>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
            </div>
          ) : upcomingTrips.length === 0 ? (
            <Card className="border-slate-100 dark:border-slate-700 border-dashed">
              <CardContent className="py-12 text-center">
                <div className="w-16 h-16 rounded-full bg-orange-50 flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-8 h-8 text-orange-500" />
                </div>
                <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white mb-2">
                  {t('dashboard.noFutureTrips')}
                </h3>
                <p className="text-slate-500 dark:text-slate-400 mb-4">
                  {t('dashboard.planNextAdventure')}
                </p>
                <Link to="/trips/new">
                  <Button className="rounded-full bg-orange-500 hover:bg-orange-600">
                    <Plus className="w-4 h-4 mr-2" />
                    {t('dashboard.createTrip')}
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className={getGridClassName(viewMode)}>
              {upcomingTrips.slice(0, 6).map((trip, index) => (
                <TripCard key={trip.id} trip={trip} index={index} viewMode={viewMode} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Join Result Modal */}
      <Dialog open={joinResultModalOpen} onOpenChange={setJoinResultModalOpen}>
        <DialogContent className="sm:max-w-md">
          {joinResult?.type === 'pending' && (
            <>
              <DialogHeader>
                <div className="mx-auto w-16 h-16 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center mb-4">
                  <Clock className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
                </div>
                <DialogTitle className="text-center text-xl">Solicitação Enviada!</DialogTitle>
                <DialogDescription className="text-center">
                  Sua solicitação para entrar em <strong>{joinResult.tripName}</strong> foi enviada ao organizador.
                </DialogDescription>
              </DialogHeader>
              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg mt-2">
                <p className="text-sm text-yellow-700 dark:text-yellow-300 text-center">
                  {joinResult.message}
                </p>
              </div>
              <div className="mt-4">
                <Button 
                  className="w-full bg-orange-500 hover:bg-orange-600" 
                  onClick={() => setJoinResultModalOpen(false)}
                  data-testid="join-result-ok-btn"
                >
                  Entendi
                </Button>
              </div>
            </>
          )}
          
          {joinResult?.type === 'approved' && (
            <>
              <DialogHeader>
                <div className="mx-auto w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
                </div>
                <DialogTitle className="text-center text-xl">Você entrou na viagem!</DialogTitle>
                <DialogDescription className="text-center">
                  Bem-vindo à viagem <strong>{joinResult.tripName}</strong>!
                </DialogDescription>
              </DialogHeader>
              <div className="mt-4 flex gap-2">
                <Button 
                  variant="outline"
                  className="flex-1" 
                  onClick={() => setJoinResultModalOpen(false)}
                >
                  Fechar
                </Button>
                <Link to={`/trip/${joinResult.tripId}`} className="flex-1">
                  <Button 
                    className="w-full bg-orange-500 hover:bg-orange-600"
                    onClick={() => setJoinResultModalOpen(false)}
                    data-testid="go-to-trip-btn"
                  >
                    Ver viagem
                  </Button>
                </Link>
              </div>
            </>
          )}
          
          {joinResult?.type === 'closed' && (
            <>
              <DialogHeader>
                <div className="mx-auto w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
                  <Lock className="w-8 h-8 text-red-600 dark:text-red-400" />
                </div>
                <DialogTitle className="text-center text-xl">Viagem Fechada</DialogTitle>
                <DialogDescription className="text-center">
                  Esta viagem não está mais aceitando novos participantes.
                </DialogDescription>
              </DialogHeader>
              <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg mt-2">
                <p className="text-sm text-red-700 dark:text-red-300 text-center">
                  {joinResult.message}
                </p>
              </div>
              <div className="mt-4">
                <Button 
                  variant="outline"
                  className="w-full" 
                  onClick={() => setJoinResultModalOpen(false)}
                >
                  Entendi
                </Button>
              </div>
            </>
          )}
          
          {joinResult?.type === 'ended' && (
            <>
              <DialogHeader>
                <div className="mx-auto w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
                  <XCircle className="w-8 h-8 text-slate-600 dark:text-slate-400" />
                </div>
                <DialogTitle className="text-center text-xl">Viagem Encerrada</DialogTitle>
                <DialogDescription className="text-center">
                  Esta viagem já foi concluída ou cancelada.
                </DialogDescription>
              </DialogHeader>
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg mt-2">
                <p className="text-sm text-slate-600 dark:text-slate-400 text-center">
                  {joinResult.message}
                </p>
              </div>
              <div className="mt-4">
                <Button 
                  variant="outline"
                  className="w-full" 
                  onClick={() => setJoinResultModalOpen(false)}
                >
                  Entendi
                </Button>
              </div>
            </>
          )}
          
          {joinResult?.type === 'error' && (
            <>
              <DialogHeader>
                <div className="mx-auto w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
                  <XCircle className="w-8 h-8 text-red-500" />
                </div>
                <DialogTitle className="text-center text-xl">Código Inválido</DialogTitle>
                <DialogDescription className="text-center">
                  Não foi possível encontrar a viagem com o código informado.
                </DialogDescription>
              </DialogHeader>
              <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg mt-2 border border-red-200 dark:border-red-800">
                <p className="text-sm text-red-600 dark:text-red-400 text-center">
                  {joinResult.message}
                </p>
              </div>
              <div className="mt-4 space-y-2">
                <Button 
                  className="w-full bg-orange-500 hover:bg-orange-600" 
                  onClick={() => {
                    setJoinResultModalOpen(false);
                    setDialogOpen(true);
                  }}
                >
                  Tentar novamente
                </Button>
                <Button 
                  variant="outline"
                  className="w-full" 
                  onClick={() => setJoinResultModalOpen(false)}
                >
                  Fechar
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Wallet Modal */}
      {selectedWalletTrip && (
        <WalletModal
          open={walletModalOpen}
          onOpenChange={setWalletModalOpen}
          tripId={selectedWalletTrip.tripId}
          userId={selectedWalletTrip.userId}
          userName={selectedWalletTrip.userName}
          isOwnWallet={true}
          formatCurrency={(value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0)}
        />
      )}
    </DashboardLayout>
  );
}
